import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, useParams } from 'react-router';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Layout, MobileLayout, Spinner, toastCaller } from '@/shared/ui';

import { composedModel } from '@/entities/composed';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import {
  getConsultationPath,
  getRecordingsPath,
  transcriptorModel,
} from '@/entities/transcriptor';

import { useRecordingSession } from '@/features/audioRecording';

import { Header, MobileHeader } from '@/widgets/Header';
import { RecordingControlsPanel } from '@/widgets/RecordingToast';
import {
  RecordingDocuments,
  RecordingWorkspace,
} from '@/widgets/TranscriptorWorkspace';

import styles from './PatientRecording.module.scss';

export const PatientRecording: FC = () => {
  const { patientID = '', recordId = '' } = useParams<{
    patientID: string;
    recordId: string;
  }>();
  const { isMobile } = useMedia();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const [failure, setFailure] = useState<'notFound' | 'failed' | null>(null);
  const { CanEditPatient, CanRequestReport } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );
  const { isReady, isAvailable } = useAppSelector(
    composedModel.selectors.selectTranscriptorAvailability,
  );
  const encounter = useAppSelector((state) =>
    transcriptorModel.selectors.selectPatientConsultationById(
      state,
      patientID,
      recordId,
    ),
  );
  const organizationId = useAppSelector(
    (state) =>
      encounter?.organizationId ||
      patientModel.selectors.selectPatientByID(patientID)(state)
        ?.OrganizationID ||
      '',
  );
  const v2Enabled = useAppSelector((state) =>
    organizationModel.selectors.selectTranscriptorV2Enabled(
      state,
      organizationId,
    ),
  );
  const session = useRecordingSession(recordId);

  useEffect(() => {
    let active = true;
    setFailure(null);
    void dispatch(
      transcriptorModel.thunks.fetchConsultationById({
        consultationId: recordId,
        patientId: patientID,
      }),
    )
      .unwrap()
      .catch((error: unknown) => {
        if (active) {
          setFailure(
            transcriptorModel.thunks.isConsultationNotFoundError(error)
              ? 'notFound'
              : 'failed',
          );
        }
      });
    return () => {
      active = false;
    };
  }, [recordId, patientID]);

  const classicEncounter = !!encounter && !encounter.transcriptorV2;
  const v2Off = v2Enabled === false && !classicEncounter;

  useEffect(() => {
    if (v2Off) {
      toastCaller({
        type: 'info',
        message: formatMessage({
          id: 'patientRecording.v2Unavailable',
          defaultMessage:
            'This recording was made in the new Transcriptor; it is available when that version is on',
        }),
      });
    }
  }, [v2Off]);

  if (classicEncounter) {
    return (
      <Navigate
        replace
        to={getConsultationPath({ patientID, consultationID: recordId })}
      />
    );
  }

  if (v2Enabled === undefined) {
    return <Spinner size={48} />;
  }

  // A failed read leaves the page while nothing records here; the recording
  // in progress stays on its page until its encounter is known to be gone.
  const left = failure === 'notFound' || (failure === 'failed' && !session);

  if (!v2Enabled || left || (isReady && !isAvailable)) {
    return <Navigate replace to={getRecordingsPath(patientID)} />;
  }

  if (!encounter && !session) {
    return <Spinner size={48} />;
  }

  const page = (
    <div className={styles.page} data-section="recording-page">
      {session ? (
        <RecordingWorkspace
          className={styles.content}
          session={session}
          controls={<RecordingControlsPanel />}
        />
      ) : (
        <RecordingDocuments
          className={styles.content}
          patientId={patientID}
          encounterId={recordId}
          encounter={encounter}
          canEdit={CanEditPatient}
          canRecord={CanRequestReport}
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileHeader />
        <MobileLayout.Main>{page}</MobileLayout.Main>
      </MobileLayout>
    );
  }

  return (
    <Layout>
      <Layout.Header>
        <Header />
      </Layout.Header>
      <Layout.Content>
        <Layout.Main>{page}</Layout.Main>
      </Layout.Content>
    </Layout>
  );
};
